import { useState } from "react";
import { useTranslation } from "react-i18next";
import { PaymentDetail } from "../../../../../interfaces/Purchases/Payments/payments";
import { GridColDef } from "@mui/x-data-grid";
import { Button, Tooltip, tooltipClasses } from "@mui/material";
import { useFormatNumber } from "../../../../../hooks/useFormatNumber";
import { useParamsFilter } from "../../../../../hooks/useParamsFilter";
import { useDates } from "../../../../../hooks/useDates";
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';

export const useDialogColumnsAuthorization = () => {
    const [t] = useTranslation("global");
    const { setFormatNumberFromTable, setFormatNumber } = useFormatNumber();
    const { ConvertParamsToBase64 } = useParamsFilter();
    const { TimeConverter } = useDates();
    const columnsPaymentDetailsAux: GridColDef<PaymentDetail>[] = [
        {
            field: 'purchase_invoice_name', headerName: t("purchaseinvoices.fields.purchase_invoice_name"), headerClassName: 'header-grid-table', flex: 1,
            renderCell(params) {
                let stringDetails = params.row.str_details ?? "";
                let tooltipContent = '';

                if (stringDetails !== "") {
                    const products = stringDetails.split(',');

                    const formattedProducts = products.map(product => {
                        const parts = product.split(' - ');
                        const unit = parseFloat(parts[0]).toFixed(2);
                        const item = parts[1].trim();
                        const price = setFormatNumber({ value: parseFloat(parts[2].replace('$', '')), decimals: 2 });
                        return `${unit} - ${item} - ${price}`;
                    });

                    tooltipContent = formattedProducts.join('\n');
                }
                return (
                    <Tooltip
                        title={
                            <span style={{ whiteSpace: 'pre-line', display: 'inline-block' }}>
                                {tooltipContent}
                            </span>
                        }
                        slotProps={{
                            popper: {
                                sx: {
                                    [`&.${tooltipClasses.popper}[data-popper-placement*="bottom"] .${tooltipClasses.tooltip}`]:
                                    {
                                        marginTop: '0px',
                                        marginBottom: '0px',
                                        marginLeft: '0px',
                                        marginRight: '0px',
                                        whiteSpace: 'nowrap',
                                        maxWidth: 'none',
                                    },
                                },
                            },
                        }}
                    >
                        <Button onClick={() => {
                            let stringConverted = ConvertParamsToBase64(`purchase_invoice_id=${params.row.purchase_invoice_id}&supplier_id=${params.row.supplier_id}`);
                            window.open(`/purchaseinvoices/view?${stringConverted}`, '_blank');
                        }}>
                            {params.row.purchase_invoice_name}
                        </Button>
                    </Tooltip>
                );
            }
        },
        {
            field: 'project_name', headerName: t("projects.fields.project_name"), headerClassName: 'header-grid-table', flex: 1,
            renderCell(params) {
                return (
                    <Button
                        disabled={(params.row.project_name) ? false : true}
                        onClick={() => {
                            if (params.row.project_id) {
                                let stringConverted = ConvertParamsToBase64(`project_id=${params.row.project_id}`);
                                window.open(`/projects/view?${stringConverted}`, '_blank');
                            }
                        }}
                    >
                        {params.row.project_name}
                    </Button>
                );
            }
        },
        {
            field: 'eta', headerName: t("projects.fields.eta"), headerClassName: 'header-grid-table', flex: 1, type: 'date',
            valueGetter(params) {
                return (params.value) ? TimeConverter(params.value) : undefined;
            },
        },
        {
            field: 'is_authorized',
            headerName: t("paymentsdetails.fields.is_authorized"),
            headerClassName: 'header-grid-table', flex: 1, align: "center", headerAlign: "center",
            renderCell(params) {
                return <>
                    {(params.row?.is_authorized) ? <CheckCircleIcon /> : (params.row?.is_authorized === false) ? <CancelIcon /> : <></>}
                </>
            }
        },
        { field: 'supplier_business_name', headerName: t("purchaseinvoices.fields.supplier_business_name"), headerClassName: 'header-grid-table', flex: 1 },
        { field: 'customer_business_name', headerName: t("projects.fields.customer"), headerClassName: 'header-grid-table', flex: 1 },
        { field: 'shipping_company', headerName: t("projects.fields.shipping_company"), headerClassName: 'header-grid-table', flex: 1 },
        { field: 'notes', headerName: t("purchaseinvoices.fields.notes"), headerClassName: 'header-grid-table', flex: 1 },
        { field: 'status_name', headerName: t("paymentsdetails.fields.status_name"), headerClassName: 'header-grid-table', flex: 1 },
        { field: 'related_currency_code', headerName: t("currencies.title-view"), headerClassName: 'header-grid-table', flex: 1 },
        {
            field: 'amount_outstanding', headerName: t("paymentsdetails.fields.amount_outstanding"), headerClassName: 'header-grid-table', flex: 1, align: 'left', headerAlign: 'left',
            valueGetter(params) {
                return setFormatNumberFromTable(params.value ?? 0);
            },
        },
        {
            field: 'total_company_currency_amount', headerName: t("companies.configuration.fields.total_company_currency_amount"), headerClassName: 'header-grid-table', flex: 1,
            valueGetter(params) {
                return setFormatNumberFromTable(params.value ?? 0);
            },
        },
        {
            field: 'payment_deadline', headerName: t("paymentsdetails.fields.payment_deadline"), headerClassName: 'header-grid-table', type: "date", flex: 1,
            valueGetter(params) {
                return (params.value) ? TimeConverter(params.value) : undefined;
            },
        },
        { field: 'user_mail_send', headerName: t("payments.fields.user_mail_send"), headerClassName: 'header-grid-table', flex: 1 },
        {
            field: 'date_mail_send', headerName: t("payments.fields.date_mail_send"), headerClassName: 'header-grid-table', flex: 1, type: 'date',
            valueGetter(params) {
                return (params.value) ? TimeConverter(params.value) : undefined;
            },
        },
        {
            field: 'payment_date', headerName: t("paymentsdetails.fields.payment_date"), headerClassName: 'header-grid-table', type: "date", flex: 1,
            valueGetter(params) {
                return (params.value) ? TimeConverter(params.value) : undefined;
            },
        },
        {
            field: 'creation_date', headerName: t("generic.creation_date"), headerClassName: 'header-grid-table', type: "date", flex: 1,
            valueGetter(params) {
                return TimeConverter(params.value);
            },
        },
        { field: 'last_update_user', headerName: t("generic.last_update_user"), headerClassName: 'header-grid-table', flex: 1 },
        {
            field: 'last_update_date', headerName: t("generic.last_update_date"), headerClassName: 'header-grid-table', type: "date", flex: 1,
            valueGetter(params) {
                return TimeConverter(params.value);
            },
        },
    ];

    return {
        columnsPaymentDetailsAux,
    }
}